import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/apiClient";
import { AudiobookCover } from "@/pages/Audiobooks";
import { getProgress, setProgress, clearProgress, fmtTime } from "@/lib/listening";
import { ArrowLeft, Headphones, ExternalLink, User, Mic, Loader2, Play } from "lucide-react";

function ytId(url = "") {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
  return m ? m[1] : "";
}

export default function AudiobookDetail() {
  const { id } = useParams();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plays, setPlays] = useState(0);
  const audioRef = useRef(null);
  const countedRef = useRef(false);
  const lastSaveRef = useRef(0);
  const saved = getProgress(id);

  useEffect(() => {
    setLoading(true);
    countedRef.current = false;
    api.get(`/audiobooks/${id}`).then((r) => { setBook(r.data); setPlays(r.data.plays || 0); })
      .catch(() => setBook(null)).finally(() => setLoading(false));
  }, [id]);

  const countPlay = () => {
    if (countedRef.current) return;
    countedRef.current = true;
    api.post(`/audiobooks/${id}/play`).then((r) => setPlays(r.data.plays)).catch(() => {});
  };

  const onLoadedMeta = () => {
    const a = audioRef.current;
    const p = getProgress(id);
    if (a && p && p.t && p.t < a.duration * 0.98) a.currentTime = p.t;
  };
  const onTimeUpdate = () => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const now = Date.now();
    if (now - lastSaveRef.current > 4000) {
      lastSaveRef.current = now;
      if (a.currentTime >= a.duration * 0.98) clearProgress(id);
      else setProgress(id, a.currentTime, a.duration);
    }
  };

  if (loading) return <main className="bg-onyx min-h-screen pt-32 text-center text-bone/50"><Loader2 className="animate-spin inline" /> Loading...</main>;
  if (!book) return <main className="bg-onyx min-h-screen pt-32 text-center text-bone/50" data-testid="audiobook-missing">Audiobook not found. <Link to="/audiobooks" className="text-gold underline">Back to library</Link></main>;

  const yt = book.youtube_id || ytId(book.external_url);

  return (
    <main className="bg-onyx min-h-screen pt-24 pb-20" data-testid="audiobook-detail-page">
      <div className="max-w-5xl mx-auto px-5 md:px-8">
        <Link to="/audiobooks" className="inline-flex items-center gap-2 text-bone/50 hover:text-gold text-sm mb-8 transition-colors" data-testid="audiobook-back">
          <ArrowLeft size={16} /> Audiobook Library
        </Link>

        <div className="grid md:grid-cols-[280px_1fr] gap-8 items-start">
          <div className="group max-w-[280px]"><AudiobookCover book={book} className="aspect-[3/4] w-full" /></div>

          <div>
            <span className="text-[10px] uppercase tracking-widest text-gold border border-gold/30 px-2 py-0.5">{book.source_label || "Audio"}</span>
            <h1 className="font-serif text-3xl md:text-4xl text-bone mt-4 leading-tight" data-testid="audiobook-title">{book.title}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-bone/60 text-sm">
              {book.author && <span className="flex items-center gap-1.5"><User size={14} className="text-gold/70" /> {book.author}</span>}
              {book.narrator && <span className="flex items-center gap-1.5"><Mic size={14} className="text-gold/70" /> {book.narrator}</span>}
              {plays > 0 && <span className="flex items-center gap-1.5" data-testid="audiobook-plays"><Play size={13} className="text-gold/70" /> {plays.toLocaleString()} plays</span>}
            </div>
            {book.description && <p className="text-bone/70 leading-relaxed mt-5">{book.description}</p>}

            {/* Player */}
            <div className="mt-7">
              {book.source_type === "upload" ? (
                <div>
                  {saved && saved.t > 5 && saved.t < saved.d * 0.98 && (
                    <p className="text-gold/80 text-xs mb-2 flex items-center gap-1.5" data-testid="audiobook-resume-note">
                      <Play size={12} /> Resuming from {fmtTime(saved.t)}
                    </p>
                  )}
                  <audio ref={audioRef} controls className="w-full" data-testid="audiobook-player"
                    src={`${API}/audiobooks/${book.id}/stream`}
                    onLoadedMetadata={onLoadedMeta} onTimeUpdate={onTimeUpdate} onPlay={countPlay}>
                    Your browser does not support audio playback.
                  </audio>
                </div>
              ) : yt ? (
                <div className="aspect-video w-full max-w-2xl border border-white/10" data-testid="audiobook-embed" onClick={countPlay}>
                  <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${yt}`} title={book.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              ) : book.external_url ? (
                <a href={book.external_url} target="_blank" rel="noreferrer" data-testid="audiobook-listen-link" onClick={countPlay}
                  className="inline-flex items-center gap-2 bg-gold text-black px-6 py-3 text-sm font-medium hover:bg-gold/90 transition-colors">
                  <Headphones size={17} /> Listen on {book.source_label || "source"} <ExternalLink size={14} />
                </a>
              ) : (
                <p className="text-bone/40 text-sm">No audio source available.</p>
              )}
            </div>
          </div>
        </div>

        {/* You might also like */}
        {book.related?.length > 0 && (
          <section className="mt-16 border-t border-white/10 pt-10" data-testid="audiobook-related">
            <h2 className="font-serif text-2xl text-bone mb-6">You Might Also Like</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {book.related.map((b) => (
                <Link key={b.id} to={`/audiobooks/${b.id}`} className="group block" data-testid="audiobook-related-card">
                  <AudiobookCover book={b} className="aspect-[3/4] w-full" />
                  <h3 className="font-serif text-base text-bone mt-2 leading-snug group-hover:text-gold transition-colors line-clamp-2">{b.title}</h3>
                  <p className="text-bone/50 text-xs mt-0.5">{b.author}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
